/**
 * Practice language picker (completion plan P5).
 *
 * Chosen before an attempt starts, for Written Chat and Live Conversation
 * alike: the server freezes the language into the attempt, so it cannot change
 * mid-conversation. Defaults to the base language.
 *
 * "Assess my <language>" is an explicit opt-in to the language-practice rubric,
 * offered only in Development and only for a language other than the base
 * language. A graded test is always assessed against its scenario's own rubric,
 * so the opt-in does not exist there.
 */

import {
  PRACTICE_LANGUAGES,
  isTargetLanguage,
  practiceLanguageOption,
} from '../config/practiceLanguages';

export interface PracticeLanguagePickerProps {
  language: string;
  onLanguageChange: (code: string) => void;
  /** Development (practice) or Testing (graded). */
  mode: 'development' | 'testing';
  languageAssessment: boolean;
  onLanguageAssessmentChange: (on: boolean) => void;
}

export function PracticeLanguagePicker({
  language,
  onLanguageChange,
  mode,
  languageAssessment,
  onLanguageAssessmentChange,
}: PracticeLanguagePickerProps) {
  const offerAssessment = mode === 'development' && isTargetLanguage(language);
  const name = practiceLanguageOption(language)?.name ?? language;
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 mb-3 text-xs text-gray-700">
      <label htmlFor="practice-language" className="font-medium">
        Conversation language
      </label>
      <select
        id="practice-language"
        value={language}
        onChange={(e) => onLanguageChange(e.target.value)}
        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs"
      >
        {PRACTICE_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>
            {l.label}
          </option>
        ))}
      </select>
      {offerAssessment && (
        <label className="inline-flex items-center gap-1.5">
          <input
            id="practice-language-assessment"
            type="checkbox"
            checked={languageAssessment}
            onChange={(e) => onLanguageAssessmentChange(e.target.checked)}
          />
          Assess my {name} (language practice rubric)
        </label>
      )}
    </div>
  );
}

export default PracticeLanguagePicker;
